import { Container } from "@chakra-ui/react";
import styles from "./solution.module.scss";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import OptimizedImage from "../../components/common/OptimizedImage";

const integrationMarkdown = `BioLean is an advanced prediction, visualization, and training solution tailored for biotech production operations, taking into account the nuances and intricacies of:

- Data availability and quality
- Process constraints
- Operator capabilities and continuous development

BioLean seamlessly integrates with your plant control systems (DCS/SCADA), ERP systems, and **Microsoft Entra ID (Azure Active Directory)** to provide real-time process data and personalized employee development. Our platform tracks individual skill progression, keeping your workforce up-to-date with the latest knowledge while maintaining a stand-alone architecture that requires no backward integration into existing infrastructure.`;

const gamificationMarkdown = `**Accelerating operator competency through research-backed gamification**

BioLean's gamification platform was developed in collaboration with [University of Copenhagen](https://comm.ku.dk/research/information-technology-and-connections/agave/) and leading biotech manufacturers to transform how operators learn complex bioprocesses.

**Key outcomes from our gamification approach:**

- **Faster onboarding**: New operators reach full competency in days rather than months
- **Enhanced engagement**: Interactive learning modules make process training inspiring and memorable
- **Reduced mentoring burden**: Self-guided gamified pathways free experienced staff for critical tasks
- **Measurable skill progression**: Track individual competencies and process knowledge in real-time

By combining game mechanics with process expertise, we help operators master complex biotech operations while maintaining the rigor required for regulatory compliance.`;

const Solution = () => {
  const { elementRef: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });
  const { elementRef: imageRef, isVisible: imageVisible } = useScrollAnimation({ threshold: 0.2 });
  const { elementRef: textRef, isVisible: textVisible } = useScrollAnimation({ threshold: 0.2 });
  const { elementRef: imageRef2, isVisible: imageVisible2 } = useScrollAnimation({ threshold: 0.2 });
  const { elementRef: textRef2, isVisible: textVisible2 } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div className={`sectionContainer ${styles.workDescription}`} id="solution">
      <Container>
        <h2
          ref={titleRef}
          className={`sectionSubtitle ${styles.workSubtitle} fade-in ${titleVisible ? 'visible' : ''}`}
        >
          How does it work?
        </h2>

        {/* First subsection: Image left, text right */}
        <div className={styles.descriptionContainer}>
          <div
            ref={imageRef}
            className={`${styles.imageContainer} fade-in-left ${imageVisible ? 'visible' : ''}`}
          >
            <OptimizedImage
              src="/media/images/how_it_works.png"
              alt="description of Biolean's process"
            />
          </div>
          <div
            ref={textRef}
            className={`${styles.markdownContainer} fade-in-right ${textVisible ? 'visible' : ''}`}
          >
            <ReactMarkdown
              className={styles.markdown}
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
              }}
            >
              {integrationMarkdown}
            </ReactMarkdown>
          </div>
        </div>

        {/* Second subsection: Text left, image right */}
        <div className={`${styles.descriptionContainer} ${styles.reversed}`}>
          <div
            ref={textRef2}
            className={`${styles.markdownContainer} fade-in-left ${textVisible2 ? 'visible' : ''}`}
          >
            <ReactMarkdown
              className={styles.markdown}
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
              }}
            >
              {gamificationMarkdown}
            </ReactMarkdown>
          </div>
          <div
            ref={imageRef2}
            className={`${styles.imageContainer} fade-in-right ${imageVisible2 ? 'visible' : ''}`}
          >
            <OptimizedImage
              src="/media/images/AGAVE_Illustration.png"
              alt="BioLean gamification platform"
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Solution;
